/* 화면 지우기, 축과 격자, 수능 그림 기본값, 엔진 반영 대기 */
import type { MathcoreApp } from './tools';
import { resetRegistry } from './tools/executor';

/** 정본 Base2D 엔진. 런타임 래퍼(mathcore.math2d.Math2D)는 엔진을 비공개로 들고 있어 UI 스토어의 base2DTool.engine 으로 닿는다 */
export function engineOf(app: MathcoreApp): any {
  const anyApp = app as any;
  // ui 게터는 createUI 전에 던진다("Need to createUI first.")
  let ui: any;
  try { ui = anyApp.ui; } catch { ui = undefined; }
  if (!ui) { try { ui = anyApp.createUI?.(); } catch { ui = undefined; } }
  try { return ui?.getStore?.().getState?.().common?.base2DTool?.engine; } catch { return undefined; }
}

/** 화면 비우기. 공개 래퍼 deleteObjectById 로 하나씩 지운다. 돌려주는 값은 지우기 전 개체 수 */
export async function clearScene(app: MathcoreApp): Promise<number> {
  resetRegistry(app);
  const publicManager: any = (app as any).objectManager;
  const count = () => (publicManager?.getAllObjects?.() ?? []).length;
  const before = count();
  if (before === 0) return 0;
  // engine.object.clear 의 C_O 메시지는 곡선 층(math-curve)이 받지 않아 곡선이 화면에 남는다. 삭제 메시지(R_O)로 지운다
  for (const o of publicManager?.getAllObjects?.() ?? []) {
    try { publicManager.deleteObjectById(String(o.id)); } catch { /* 부모와 함께 지워진 개체 */ }
  }
  // 반영은 비동기라 목록이 빌 때까지 잠시 기다린다
  for (let i = 0; i < 20 && count() > 0; i += 1) await new Promise((resolve) => setTimeout(resolve, 50));
  return before;
}

/** 좌표축과 격자 켜고 끄기. 축은 settingManager.axis.x/y.visibleType, 눈금은 axis.visibleUnit, 축 이름은 axis.visibleAxisName, 격자는 settingManager.grid.primary/secondary.visible.
 * 캔버스가 다음 문항까지 남으므로 켤 때도 값을 되돌린다 */
export function applyAxesAndGrid(app: MathcoreApp, axes: boolean, grid: boolean): string {
  const notes: string[] = [];
  try {
    const setting: any = (app as any).settingManager;
    if (setting?.axis) {
      if (setting.axis.x) setting.axis.x.visibleType = axes ? 'default' : 'none';
      if (setting.axis.y) setting.axis.y.visibleType = axes ? 'default' : 'none';
      // 축 눈금은 고정으로 보이고 단위는 1 이다(사용자 지시 2026-10-19). gridUnitFix 가 없으면 카메라에 따라 2, 5 로 바뀐다(실측)
      setting.axis.visibleUnit = true;
      setting.axis.gridUnitFix = true;
      setting.axis.xAxisUnit = 1;
      setting.axis.yAxisUnit = 1;
      setting.axis.visibleAxisName = axes;
    }
    if (setting?.grid) {
      if (setting.grid.primary) setting.grid.primary.visible = grid;
      if (setting.grid.secondary) setting.grid.secondary.visible = grid;
    }
    if (!axes) notes.push('축 숨김');
    if (!grid) notes.push('격자 숨김');
  } catch (error) {
    notes.push(`축과 격자 설정 실패: ${(error as Error).message}`);
  }
  try {
    // engine.setGridVisible(false) 는 축까지 함께 숨기므로 격자는 settingManager.grid 로만 끄고 여기서는 다시 그리기만 한다
    engineOf(app)?.render?.();
  } catch { /* 비공개 경로 실패는 넘어간다 */ }
  return notes.length ? ` · ${notes.join(' · ')}` : '';
}

/** math2d 위 글자 크기(사용자 지시 2026-10-15: 18 에서 시작, 안 읽히면 20~24 로 올린다) */
export const MATH_TEXT_SIZE = 18;

/** math2d 위 글자 글꼴. 수능 문제지 글꼴(신명 중명조, HyhwpEQ)은 한컴 번들이라 쓸 수 없어 math2d CDN 글꼴 중 가장 가까운 명조를 쓴다(사용자 지시 2026-10-15) */
export const MATH_TEXT_FONT = 'Batang';

export const BLACK: [number, number, number, number] = [0.1, 0.1, 0.1, 1];

/** 새로 생기는 개체의 기본값을 수능 그림 관례에 맞춘다(레퍼런스는 data/archive 의 기출 그림, 사용자 지시 2026-10-15).
 * 글자는 MATH_TEXT_SIZE, MATH_TEXT_FONT. 선은 검정. 축 눈금(단위 1)과 격자는 보임(사용자 지시 2026-10-19) */
export function applyCsatDefaults(app: MathcoreApp): void {
  const configs = engineOf(app)?.configs;
  if (configs) {
    configs.nametextSize = MATH_TEXT_SIZE;
    configs.textSize = MATH_TEXT_SIZE;
    configs.nametextFont = MATH_TEXT_FONT;
    configs.textFont = MATH_TEXT_FONT;
    for (const key of ['linearColor', 'circleColor', 'algebraicColor', 'bezierColor', 'drawingColor']) configs[key] = [...BLACK];
  }
  applyAxesAndGrid(app, true, true);
}

/** 엔진에 예약된 작업이 처리될 때까지 기다린다. 팩토리 생성과 프록시 대입은 core dispatch 로 모였다가 다음 틱에 반영되고, 반영 전에 읽으면 옛 상태가 나온다(mathcore core/index.ts dispatch) */
/** 곡선 표본이 올 때까지 기다린다. 대수 곡선은 렌더러의 작업자(worker)가 비동기로 표본을 뜨고 개체 관리자의 polynomialPathMap 에 넣는다(packages/renderer math-curve.ts resolveJob → manager.ts setPolynomialPathMap).
 * 새 페이지에서는 작업자 스크립트를 처음 받느라 늦어, 표본 전에 접선이나 교점을 세우면 무효로 선다(실측 2026-10-19: 새 페이지 S3 에서 교점 A, B, C 가 빠짐). 영역(부등식, 정적분)과 그릴 수 없는 식은 기다리지 않는다 */
export async function waitForCurves(app: MathcoreApp, maxMs = 5000): Promise<void> {
  const manager = engineOf(app)?.objectManager;
  if (!manager || typeof manager.getPolynomialPathMap !== 'function') return;
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    const objects = (app.objectManager?.getAllObjects?.() ?? []) as { id: string; type?: string; isValid?: boolean; inequalityExists?: boolean; expressionType?: number }[];
    const curves = objects.filter((o) => o.type === 'polynomial' && o.isValid !== false && !o.inequalityExists && o.expressionType !== 0);
    const ready = curves.every((o) => { const path = manager.getPolynomialPathMap(o.id); return Array.isArray(path) && path.length > 0; });
    if (ready) return;
    await new Promise((resolve) => { setTimeout(resolve, 50); });
  }
}

export async function settleEngine(app: MathcoreApp, maxTicks = 50): Promise<void> {
  const engine = engineOf(app);
  const tick = () => new Promise((resolve) => { setTimeout(resolve, 0); });
  for (let i = 0; i < maxTicks; i += 1) {
    await tick();
    // 처리 중 새로 예약된 작업(RO 로 이어진 재평가)까지 비도록 한 틱 더 본다
    if (!engine?.isPreparingPublish) { await tick(); if (!engine?.isPreparingPublish) return; }
  }
}

/** 마운트 때의 epsilon(픽셀당 월드 단위). 이보다 좁게 확대하지 않는다 */
let mountEpsilon = 0;
export function rememberMountEpsilon(app: MathcoreApp): void {
  const e = Number(app.cameraManager?.epsilon?.[0]);
  if (Number.isFinite(e) && e > 0) mountEpsilon = e;
}

/** 대수 곡선이 직선인가: 엔진 표본(getPolynomialPathMap)이 한 직선 위에 놓이면 그 표본, 아니면 null. 표본이 셋 미만이면 모른다(null) */
export function straightSamples(app: MathcoreApp, id: string): [number, number][] | null {
  const manager: any = engineOf(app)?.objectManager;
  const path: unknown = manager?.getPolynomialPathMap?.(id);
  if (!Array.isArray(path)) return null;
  const pts = (path as unknown[]).filter((p): p is [number, number] => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (pts.length < 3) return null;
  const a = pts[0]; const b = pts[pts.length - 1];
  const dx = b[0] - a[0]; const dy = b[1] - a[1];
  const span = Math.hypot(dx, dy);
  if (span === 0) return null;
  const far = Math.max(...pts.map((p) => Math.abs((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / span));
  return far <= span * 1e-6 ? pts : null;
}

/** 카메라를 원점, 선 점 전부, 점 구간 안의 곡선 표본, 그리고 extra 좌표가 들어오게 맞춘다. fill 은 화면에서 내용이 차지하는 비율(작을수록 넓게 본다).
 * 엔진은 접점, 교점을 카메라가 보는 창 안에서만 찾는다(실측 2026-10-20: 같은 호출이 그림판이 작은 페이지에서는 무효, 전체 화면 페이지에서는 유효). 그래서 실행 직전에는 넓게(fill 작게), 캡처 직전에는 보기 좋게(fill 크게) 맞춘다 */
export function fitCamera(app: MathcoreApp, fill: number, extra: [number, number][] = []): void {
  const camera = app.cameraManager;
  if (!camera || !Array.isArray(camera.epsilon)) return;
  const objects: any[] = (app as any).objectManager?.getAllObjects?.() ?? [];
  const points: [number, number][] = objects
    .filter((o: any) => o.type === 'point' && o.isValid && Array.isArray(o.position) && o.position.every((v: unknown) => Number.isFinite(v)))
    .map((o: any) => [o.position[0], o.position[1]] as [number, number]);
  const anchors = [...points, ...extra];
  const xs = [0, ...anchors.map((p) => p[0])];
  const ys = [0, ...anchors.map((p) => p[1])];
  if (anchors.length) {
    const lo = Math.min(...xs); const hi = Math.max(...xs);
    const manager: any = engineOf(app)?.objectManager;
    const pathOf = manager?.getPolynomialPathMap;
    for (const o of objects) {
      if (o.type !== 'polynomial' || o.isValid === false || o.inequalityExists || typeof pathOf !== 'function') continue;
      // 직선꼴 식(x=0, y=0 같은)은 끝이 없어 표본이 세로나 가로로 한없이 뻗는다. 그 표본을 넣으면 한 축의 epsilon 이 터져 그림이 납작해진다(실측 2026-10-21 experience/final-export-tangent.png)
      if (straightSamples(app, o.id)) continue;
      const path: unknown = pathOf.call(manager, o.id);
      if (!Array.isArray(path)) continue;
      for (const p of path as unknown[]) {
        if (!Array.isArray(p) || !Number.isFinite(p[0]) || !Number.isFinite(p[1]) || p[0] < lo || p[0] > hi) continue;
        xs.push(p[0]); ys.push(p[1]);
      }
    }
  }
  const spanX = Math.max(...xs) - Math.min(...xs);
  const spanY = Math.max(...ys) - Math.min(...ys);
  const width = Number(camera.width) || 0;
  const height = Number(camera.height) || 0;
  const epsilonX = Math.max(mountEpsilon, width ? spanX / (width * fill) : 0);
  const epsilonY = Math.max(mountEpsilon, height ? spanY / (height * fill) : 0);
  if (camera.epsilon[0] !== epsilonX || camera.epsilon[1] !== epsilonY) camera.epsilon = [epsilonX, epsilonY];
  if (anchors.length) camera.cameraPosition = [(Math.max(...xs) + Math.min(...xs)) / 2, (Math.max(...ys) + Math.min(...ys)) / 2];
  try { engineOf(app)?.render?.(); } catch { /* 비공개 경로 실패는 넘어간다 */ }
}
